import { FormContainer } from '@strata/react-form-container';
import { FormSection } from '@strata/react-form-section';
import {
    ACCEPTED_QUESTION_ID,
    PolicyCompleteText,
    QuestionGraph,
} from '@strata/react-question-tree';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { QuestionLogic } from 'pages/clinicalPolicy/QuestionLogic';
import { handleError } from 'services/ErrorManagement';

export const LogicTab = ({ questions = undefined, updateQuestions, readOnly }) => {
    const [flowQuestions, setFlowQuestions] = useState(questions);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFlowQuestions(questions);
    }, [questions]);

    const questionSelectOptions = useMemo(
        () => [
            ...(questions?.map((question) => ({
                value: question.question_id,
                name: `${question.question_id}. ${question.question_text.replaceAll(
                    `<br />`,
                    ' ',
                )}`,
            })) ?? []),
            { value: ACCEPTED_QUESTION_ID, name: `Ending: ${PolicyCompleteText.POLICY_MET}` },
        ],
        [questions],
    );
    return (
        <div className="sds_policy-edit">
            <div className="sds_policy-edit__column" style={{ width: '55%' }}>
                <Formik
                    initialValues={{
                        questions: questions?.map((question) => ({
                            ...question,
                            option_validation: question.option_validation?.map((rule) => ({
                                ...rule,
                                conditions: [
                                    ...rule.rule.matchAll(
                                        /(?:(?<condition>NOT|AND|OR)? ?(?<startParen>\()?(?:\{(?<control>COUNT|INTEGERINPUT)? ?(?<operator>[!=<>]{1,2})? ?(?<value>\d+)\})(?<endParen>\))?|^$)/g,
                                    ),
                                ].map((match) => ({
                                    ...match.groups,
                                    value: parseInt(match.groups.value, 10),
                                })),
                            })),
                        })),
                    }}
                    enableReinitialize
                    validationSchema={Yup.object({
                        questions: Yup.array(
                            Yup.object({
                                option_validation: Yup.array(
                                    Yup.object({
                                        question_id: Yup.number().required(
                                            validationStrings.requiredField.errorMessage,
                                        ),
                                        conditions: Yup.array(
                                            Yup.object().shape(
                                                {
                                                    condition: Yup.string(),
                                                    control: Yup.string().when('operator', {
                                                        is: (operator) => operator,
                                                        then: (schema) =>
                                                            schema.required(
                                                                validationStrings.requiredField
                                                                    .errorMessage,
                                                            ),
                                                    }),
                                                    operator: Yup.string().when('control', {
                                                        is: (control) => control,
                                                        then: (schema) =>
                                                            schema.required(
                                                                validationStrings.requiredField
                                                                    .errorMessage,
                                                            ),
                                                    }),
                                                    value: Yup.number()
                                                        .required(
                                                            validationStrings.requiredField
                                                                .errorMessage,
                                                        )
                                                        .min(
                                                            0,
                                                            validationStrings.number
                                                                .positiveNumbersOnly,
                                                        ),
                                                },
                                                ['operator', 'control'],
                                            ),
                                        ),
                                    }),
                                ),
                            }),
                        ),
                    })}
                    onSubmit={async (values) => {
                        try {
                            setFlowQuestions();
                            const updatedQuestions = values.questions.map((question) => ({
                                ...question,
                                option_validation: question.option_validation.map((rule) => ({
                                    question_id: rule.question_id,
                                    rule:
                                        rule.rule === '{}'
                                            ? '{}'
                                            : rule.conditions
                                                  .map(
                                                      ({
                                                          condition,
                                                          startParen,
                                                          control,
                                                          operator,
                                                          value,
                                                          endParen,
                                                      }) => {
                                                          const addSpace = (str) =>
                                                              str ? `${str} ` : '';

                                                          return `${addSpace(condition)}${
                                                              startParen ?? ''
                                                          }{${addSpace(control)}${addSpace(
                                                              operator,
                                                          )}${value}}${endParen ?? ''}`;
                                                      },
                                                  )
                                                  .join(' '),
                                })),
                            }));
                            await updateQuestions(updatedQuestions);
                        } catch (err) {
                            setFlowQuestions(questions);
                            handleError('Error updating question logic.', err);
                        }
                    }}
                >
                    {(formik) =>
                        !questions ? (
                            <FormSection sectionTitle="Logic" isLoading />
                        ) : (
                            <FormContainer
                                formTitle="Logic"
                                onFormSubmit={formik.handleSubmit}
                                onSecondaryButtonClick={formik.handleReset}
                                isSubmitting={
                                    !(formik.dirty && formik.isValid) ||
                                    formik.isSubmitting ||
                                    readOnly
                                }
                                formLeftContent={
                                    <div>
                                        {formik.values.questions?.map((question, index) => (
                                            <QuestionLogic
                                                key={question.question_id}
                                                readOnly={readOnly}
                                                question={question}
                                                questionIndex={index}
                                                questionSelectOptions={questionSelectOptions.filter(
                                                    (option) =>
                                                        question.question_id !== option.value,
                                                )}
                                            />
                                        ))}
                                    </div>
                                }
                            />
                        )
                    }
                </Formik>
            </div>
            <div
                className="sds_policy-edit__column sds_policy-edit__preview-pane"
                style={{ width: '45%' }}
            >
                <FormSection
                    sectionTitle="Preview"
                    isLoading={!flowQuestions}
                    content={<QuestionGraph questions={flowQuestions} />}
                />
            </div>
        </div>
    );
};

LogicTab.propTypes = {
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    updateQuestions: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
};
